import { categoryColour, formatCount, tt, type CategoryColour } from '@agos/shared'

/**
 * `/sales-channels` — §9.5's channel administration, which has no legacy counterpart at all.
 *
 * §10A.1 gave the backend a `sales_channels` table and `SalesChannel`'s own docblock says how to use it:
 *
 * > Ask what a channel **does**, never what it is called. `channel.usesDrivers()` keeps working when a
 * > franchise adds a fourth channel; `"delivery".equals(type)` does not.
 *
 * Nothing in the frontend has been able to edit those capabilities, so a franchise adding a channel had to
 * do it in SQL. This screen is that editor — and it is a **justified EXTRA** in the §12.2 parity diff,
 * citing this section.
 *
 * **The seven flags are opaque names, and that is the actual problem.** `onPremise`, `usesTables` and
 * `allowsDeferredPayment` say nothing about what they do to an order, and each of them changes whether an
 * order can be closed. What each one decides is taken from the backend's own code, not guessed:
 *
 * | Flag | What it decides |
 * |---|---|
 * | `requiresDeliveryAddress` | whether an order counts as a delivery for the address book, the customer directory and the delay SMS |
 * | `usesDrivers` | `OrderStatusService`: moving an order to *on the way* requires a driver |
 * | `usesTables` | `requiresWaiter()` — only a waiter may close it — **and** `waitsForKitchen()` — it cannot be settled mid-service |
 * | `onPremise` | `requiresHandoverOwner()` — somebody must own the money before the order closes; also picks the VAT slot |
 * | `availableOnWeb` | whether a customer on the website may choose this channel |
 * | `allowsDeferredPayment` | whether orders may stay unpaid and accumulate on a tab (§10A's *na sekeru*) |
 *
 * **FB-181.** Two of the four live channels carry a flag their own orders contradict, and nothing anywhere
 * says so. `bar` has `usesDrivers: true`, and **137 of its 1 082 orders** have a driver — 12,7 %; the other
 * 945 were handed over the counter. `delivery` has `requiresDeliveryAddress: true` and all 7 436 of its
 * orders have one, which is consistent — but **122 of them have no driver** while `usesDrivers` is true.
 * A flag is a rule for the *next* order, so neither is a defect on its own; both are worth seeing before
 * somebody changes one, because the flag decides whether an order can be closed at all.
 */

export interface Channel {
  id: number
  name: string
  slug: string
  requiresDeliveryAddress: boolean
  usesDrivers: boolean
  usesTables: boolean
  onPremise: boolean
  availableOnWeb: boolean
  allowsDeferredPayment: boolean
  /** §10A.87 — paid and printed at the counter, so never on a board. */
  settlesImmediately: boolean
  sortOrder: number
  isActive: boolean
  /** §9.38. `null` for none, and for a value off the palette — `categoryColour`'s rule. */
  colour: CategoryColour | null
  /**
   * §10A.17 — the fiscal register this channel's receipts print on.
   *
   * `null` for *nobody has chosen*, in which case the server's capability rule decides. An id this
   * build does not know reads as null too, exactly as an off-palette colour does: the screen should
   * offer what it can honour rather than show a value it cannot explain.
   */
  kasaId: KnownRegister | null
}

/**
 * The registers this build knows — `FiskalRegisters.DINING_ROOM` and `DELIVERY`, backend-side.
 *
 * Two constants rather than rows, so the list is written down here too. If a third is ever
 * configured, this is the one place that has to learn it.
 */
export const KNOWN_REGISTERS = ['restauracia', 'rozvoz'] as const
export type KnownRegister = (typeof KNOWN_REGISTERS)[number]

export function knownRegister(value: unknown): KnownRegister | null {
  return typeof value === 'string' && (KNOWN_REGISTERS as readonly string[]).includes(value.trim())
    ? (value.trim() as KnownRegister)
    : null
}

/** What each register is called on screen. The ids are backend constants, not labels. */
export function registerLabel(register: KnownRegister): string {
  return register === 'rozvoz' ? tt('Kasa Rozvoz') : tt('Kasa Reštaurácia')
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function channelsOf(payload: unknown): Channel[] {
  if (!Array.isArray(payload)) return []
  return payload
    .flatMap((entry) => {
      const row = entry as Record<string, unknown>
      if (typeof row.id !== 'number') return []
      return [
        {
          id: row.id,
          name: text(row.name) ?? '',
          slug: text(row.slug) ?? '',
          requiresDeliveryAddress: row.requiresDeliveryAddress === true,
          usesDrivers: row.usesDrivers === true,
          usesTables: row.usesTables === true,
          onPremise: row.onPremise === true,
          availableOnWeb: row.availableOnWeb === true,
          allowsDeferredPayment: row.allowsDeferredPayment === true,
          settlesImmediately: row.settlesImmediately === true,
          sortOrder: typeof row.sortOrder === 'number' ? row.sortOrder : 0,
          isActive: row.isActive === true,
          colour: categoryColour(row.color),
          kasaId: knownRegister(row.kasaId),
        },
      ]
    })
    .sort((left, right) => left.sortOrder - right.sortOrder || left.id - right.id)
}

// ─── The seven flags, and what each one does ─────────────────────────────────
//
// §10A.87 made it seven: `settlesImmediately` is the owner's *Do ruky*. Every other flag here says
// what an order **needs**; that one says when it is **finished**.

export type CapabilityKey =
  | 'requiresDeliveryAddress'
  | 'usesDrivers'
  | 'usesTables'
  | 'onPremise'
  | 'availableOnWeb'
  | 'allowsDeferredPayment'
  | 'settlesImmediately'

export const CAPABILITIES: readonly CapabilityKey[] = [
  'requiresDeliveryAddress',
  'usesDrivers',
  'usesTables',
  'onPremise',
  'availableOnWeb',
  'allowsDeferredPayment',
  'settlesImmediately',
]

export function capabilityLabel(key: CapabilityKey): string {
  if (key === 'requiresDeliveryAddress') return tt('Vyžaduje adresu')
  if (key === 'usesDrivers') return tt('Rozváža šofér')
  if (key === 'usesTables') return tt('Obsluhuje stoly')
  if (key === 'onPremise') return tt('Na prevádzke')
  if (key === 'availableOnWeb') return tt('Dostupné na webe')
  if (key === 'allowsDeferredPayment') return tt('Platba neskôr')
  return tt('Zaplatí sa hneď')
}

/** What the flag changes about an order, taken from the backend rather than from the name. */
export function capabilityMeaning(key: CapabilityKey): string {
  if (key === 'requiresDeliveryAddress') {
    return tt('Objednávka sa počíta ako rozvoz - do adresára zákazníka, do prehľadu a do SMS o meškaní.')
  }
  if (key === 'usesDrivers') {
    return tt('Bez prideleného šoféra sa objednávka nedá prepnúť na „na ceste“.')
  }
  if (key === 'usesTables') {
    return tt('Uzavrieť ju smie len čašník a až keď kuchyňa dokončí - stôl sa nedá zúčtovať uprostred obsluhy.')
  }
  if (key === 'onPremise') {
    return tt('Peniaze musí niekto prevziať - objednávka sa nedá uzavrieť bez majiteľa. Rozhoduje aj o sadzbe DPH.')
  }
  if (key === 'availableOnWeb') {
    return tt('Zákazník si tento kanál môže vybrať na webe. Bez neho ho vidí len obsluha.')
  }
  if (key === 'allowsDeferredPayment') {
    return tt('Objednávky môžu ostať nezaplatené a načítavať sa na účet („na sekeru“).')
  }
  return tt(
    'Po uložení sa hneď otvorí platba a doklad - objednávka sa neobjaví na nástenke Rozvoz ani v Otvorených stoloch.',
  )
}

export function capabilityOf(channel: Channel, key: CapabilityKey): boolean {
  return channel[key]
}

// ─── FB-181: what the orders actually did ────────────────────────────────────

/**
 * How many orders a channel carries.
 *
 * **Two endpoints answer this in two shapes, and they are not interchangeable.** `/analytics/revenue` sends
 * `channelSplit` keyed by **`type`**; `/dashboard/stats` sends `revenueByChannel` keyed by **`slug`**. They
 * hold the same value — `orders.type` stores the channel's slug — under different names, so both are read
 * rather than one being assumed. Getting this wrong is silent: the block simply is not there and every
 * channel reports *no orders*, which is exactly what a genuinely unused channel reports.
 *
 * There is no endpoint that answers *how many bar orders actually had a driver*, so FB-181's other half is
 * filed as a backend item rather than derived from something that does not mean it.
 */
export interface ChannelTrade {
  slug: string
  orders: number
}

export function tradeOf(payload: unknown): ChannelTrade[] {
  const source = (payload ?? {}) as Record<string, unknown>
  const rows = Array.isArray(source.channelSplit)
    ? source.channelSplit
    : Array.isArray(source.revenueByChannel)
      ? source.revenueByChannel
      : []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    // `slug` on the dashboard's block, `type` on the analytics one. Same value.
    const slug = text(row.slug) ?? text(row.type)
    if (slug === null) return []
    return [{ slug, orders: typeof row.orders === 'number' ? row.orders : 0 }]
  })
}

export function ordersFor(slug: string, trade: readonly ChannelTrade[]): number | null {
  const match = trade.find((row) => row.slug === slug)
  return match === undefined ? null : match.orders
}

/**
 * Whether switching this channel off, or deleting it, would strand trade.
 *
 * `DELETE /sales-channels/{id}` is documented *"Delete a sales channel that has no orders"*, so the server
 * refuses one that does. Saying so before the click is better than a red toast after it.
 */
export function deleteBlocked(channel: Channel, trade: readonly ChannelTrade[]): boolean {
  return (ordersFor(channel.slug, trade) ?? 0) > 0
}

export function deleteWarning(channel: Channel, trade: readonly ChannelTrade[]): string {
  const orders = ordersFor(channel.slug, trade)
  if (orders !== null && orders > 0) {
    return `${tt('Tento kanál nesie')} ${formatCount(orders)} ${tt('objednávok za zvolené obdobie, takže ho server zmazať nedovolí. Vypnite ho namiesto toho - vypnutý kanál sa nedá vybrať pre novú objednávku a staré ostanú čitateľné.')}`
  }
  return tt(
    'Za zvolené obdobie nemá objednávky. Server odmietne mazanie, ak nejaké má z inej doby - v takom prípade ho vypnite.',
  )
}

export function deactivateWarning(channel: Channel, trade: readonly ChannelTrade[]): string {
  const orders = ordersFor(channel.slug, trade)
  const carried =
    orders === null || orders === 0
      ? tt('Za zvolené obdobie cezeň nešla ani jedna objednávka.')
      : `${tt('Za zvolené obdobie cezeň prešlo')} ${formatCount(orders)} ${tt('objednávok.')}`
  return `${carried} ${tt('Vypnutý kanál sa nedá vybrať pre novú objednávku; hotové objednávky ostávajú v prehľadoch tak, ako sú.')}`
}

// ─── The form ────────────────────────────────────────────────────────────────

export interface ChannelForm {
  name: string
  slug: string
  requiresDeliveryAddress: boolean
  usesDrivers: boolean
  usesTables: boolean
  onPremise: boolean
  availableOnWeb: boolean
  allowsDeferredPayment: boolean
  settlesImmediately: boolean
  sortOrder: string
  isActive: boolean
  /** The empty string is *no colour*, which is what the backend stores as null. */
  colour: string
  /** §10A.17. The empty string is *use the rule*, which the backend stores as null. */
  kasaId: string
}

export function formOf(channel: Channel): ChannelForm {
  return {
    name: channel.name,
    slug: channel.slug,
    requiresDeliveryAddress: channel.requiresDeliveryAddress,
    usesDrivers: channel.usesDrivers,
    usesTables: channel.usesTables,
    onPremise: channel.onPremise,
    availableOnWeb: channel.availableOnWeb,
    allowsDeferredPayment: channel.allowsDeferredPayment,
    settlesImmediately: channel.settlesImmediately,
    sortOrder: String(channel.sortOrder),
    isActive: channel.isActive,
    colour: channel.colour ?? '',
    kasaId: channel.kasaId ?? '',
  }
}

export function emptyForm(existing: readonly Channel[]): ChannelForm {
  const highest = existing.reduce((most, channel) => Math.max(most, channel.sortOrder), 0)
  return {
    name: '',
    slug: '',
    requiresDeliveryAddress: false,
    usesDrivers: false,
    usesTables: false,
    onPremise: true,
    availableOnWeb: false,
    allowsDeferredPayment: false,
    settlesImmediately: false,
    sortOrder: String(highest + 10),
    isActive: true,
    colour: '',
    kasaId: '',
  }
}

export type ChannelProblem =
  | { kind: 'no-name' }
  | { kind: 'no-slug' }
  | { kind: 'bad-slug' }
  | { kind: 'duplicate-slug'; other: string }
  | { kind: 'sort-not-whole' }
  | { kind: 'address-without-driver' }
  | { kind: 'deferred-without-tables' }

/**
 * What the form refuses, and two combinations it questions.
 *
 * The slug is what `SalesChannel.bySlug` looks a channel up by and what `orders.type` stores, so a duplicate
 * would make two channels indistinguishable to every query in the backend. The two questioned pairs are not
 * refused — a franchise may genuinely want them — but they are the ones that leave an order unclosable.
 */
export function problemsOf(form: ChannelForm, others: readonly Channel[]): ChannelProblem[] {
  const problems: ChannelProblem[] = []
  if (form.name.trim() === '') problems.push({ kind: 'no-name' })
  const slug = form.slug.trim()
  if (slug === '') {
    problems.push({ kind: 'no-slug' })
  } else if (!/^[a-z][a-z0-9_]*$/.test(slug)) {
    problems.push({ kind: 'bad-slug' })
  } else {
    const clash = others.find((channel) => channel.slug === slug)
    if (clash !== undefined) problems.push({ kind: 'duplicate-slug', other: clash.name })
  }
  if (!/^-?\d+$/.test(form.sortOrder.trim())) problems.push({ kind: 'sort-not-whole' })

  // An address-bearing channel with no driver: `OrderStatusService` never asks for one, so the order can go
  // *on the way* with nobody driving it, and the wallet has nobody to attribute the cash to.
  if (form.requiresDeliveryAddress && !form.usesDrivers) {
    problems.push({ kind: 'address-without-driver' })
  }
  // Deferred payment is a tab, and `OrderTabService` opens one against a table.
  if (form.allowsDeferredPayment && !form.usesTables) {
    problems.push({ kind: 'deferred-without-tables' })
  }
  return problems
}

/** The two questioned pairs are warnings; everything else stops the save. */
export function isBlocking(problem: ChannelProblem): boolean {
  return problem.kind !== 'address-without-driver' && problem.kind !== 'deferred-without-tables'
}

export function canSave(form: ChannelForm, others: readonly Channel[]): boolean {
  return problemsOf(form, others).filter(isBlocking).length === 0
}

export function channelPayload(form: ChannelForm): Record<string, unknown> {
  return {
    name: form.name.trim(),
    slug: form.slug.trim(),
    requiresDeliveryAddress: form.requiresDeliveryAddress,
    usesDrivers: form.usesDrivers,
    usesTables: form.usesTables,
    onPremise: form.onPremise,
    availableOnWeb: form.availableOnWeb,
    allowsDeferredPayment: form.allowsDeferredPayment,
    sortOrder: Number(form.sortOrder.trim()),
    isActive: form.isActive,
    // '' is a person clearing it; the backend stores that as null.
    color: form.colour,
    // §10A.17. Same rule: clearing it means *go back to the capability rule*, not *the dining room*.
    kasaId: form.kasaId === '' ? null : form.kasaId,
  }
}

// `isUnreachable` was here and is deleted — §9.35, 2026-08-06. It returned `!channel.isActive`, under
// a docblock promising *"switched off, or on but offered nowhere"* — and the second half is not a
// condition. A channel that is on but not `availableOnWeb` is reachable: the staff pick it, which is
// what `reachNote` below says in so many words. So the name and the comment described a rule the body
// did not have, and the body was one negation any caller could write.
//
// `reachNote` is the thing the screen actually needed and the thing it uses.

export function reachNote(channel: Channel): string {
  if (!channel.isActive) return tt('Vypnutý - nedá sa vybrať pre novú objednávku.')
  if (channel.availableOnWeb) return tt('Vyberá si ho obsluha aj zákazník na webe.')
  return tt('Vyberá si ho len obsluha - na webe sa neponúka.')
}
